/*
 * Copyright (c) 2020. American Express Company
 */

import _ from 'lodash';
import BwService from './bw-data.service';
import { IncomeSourceModel, TransactionIncomeSourceValueModel } from 'src/types/income-source.model';
import { UserModel } from 'src/types/user.model';
import { DateTimeFormatter, LocalDate } from '@js-joda/core';
import { TransactionModel } from 'src/types/transaction.model';
import FinanceService from './finance.service';
import UserService from './user.service';
import { OTHER_INCOME } from 'src/types/onboarding.enum';

export class IncomeSourceParams {
    name?: string;
    incomeUid?: string;
    amount?: number;
    frequency: string;
    startDate: string;
}

export class UpsertIncomeParams {
    user: UserModel;
    amount: number;
    existingIncome: IncomeSourceModel;
    nonTaxIncome?: number;
}

export default class IncomeSourceService extends BwService {
    public static async createIncomeSource(incomeSource: IncomeSourceModel): Promise<IncomeSourceModel> {
        incomeSource = IncomeSourceModel.dateify(incomeSource);
        const args: IncomeSourceParams = {
            name: incomeSource.name,
            amount: incomeSource.annualizedIncome || 0,
            frequency: incomeSource.frequency || 'year',
            startDate: incomeSource.startDate ? incomeSource.startDate.format(DateTimeFormatter.ISO_LOCAL_DATE) : null
        };

        const response: IncomeSourceModel = await this.post('/incomes', args);
        return IncomeSourceModel.dateify(response);
    }

    public static async updateIncomeSource(incomeSource: IncomeSourceModel): Promise<IncomeSourceModel> {
        incomeSource = IncomeSourceModel.dateify(incomeSource);
        const args: IncomeSourceParams = {
            name: incomeSource.name,
            amount: incomeSource.annualizedIncome,
            frequency: incomeSource.frequency || 'year',
            startDate: incomeSource.startDate ? incomeSource.startDate.format(DateTimeFormatter.ISO_LOCAL_DATE) : null
        };

        const response: IncomeSourceModel = await this.put('/incomes/' + incomeSource.incomeUid, args);
        return IncomeSourceModel.dateify(response);
    }

    public static async getIncomeSource(incomeUid: string): Promise<IncomeSourceModel> {
        if (!incomeUid) return null;

        return _.find(await this.getIncomeSources(), { incomeUid });
    }

    public static async getIncomeSources(): Promise<IncomeSourceModel[]> {
        let incomeSources: IncomeSourceModel[] = await this.get('/incomes');
        incomeSources = incomeSources.map((incomeSource) => IncomeSourceModel.dateify(incomeSource));
        return incomeSources;
    }

    public static async deleteIncomeSource(incomeSource: IncomeSourceModel): Promise<number> {
        return await this.delete('/incomes/' + incomeSource.incomeUid);
    }

    /** Retrieve the income sources that are derived from transactions */
    public static getTransactionIncomes(
        transactions: TransactionModel[],
        incomeSources: IncomeSourceModel[]
    ): IncomeSourceModel[] {
        let transactionIncomes: IncomeSourceModel[] = _.filter(
            incomeSources,
            (incomeSource: IncomeSourceModel) => !incomeSource.userCreated
        );
        const transactionIncomesByValue: any = this.getTransactionIncomesByValue(transactions);
        transactionIncomes = _.filter(transactionIncomes, (transactionIncome: IncomeSourceModel) => {
            return transactionIncome.incomeUid in transactionIncomesByValue;
        });
        return transactionIncomes;
    }

    /** Retrieve the income sources that are manually added */
    public static getManualIncomes(incomeSources: IncomeSourceModel[]): IncomeSourceModel[] {
        return _.filter(incomeSources, (incomeSource: IncomeSourceModel) => incomeSource.userCreated);
    }

    /** Retrieve the total annualized amount of all manual incomes */
    public static getManualIncomesTotal(incomeSources: IncomeSourceModel[]): number {
        const manualIncomeSources: IncomeSourceModel[] = this.getManualIncomes(incomeSources);
        return _.sumBy(manualIncomeSources, 'annualizedIncome');
    }

    /** Retrieve each transaction income's total amount and store it an object */
    public static getTransactionIncomesByValue(transactions: TransactionModel[]): TransactionIncomeSourceValueModel {
        const transactionIncomesByValue: TransactionIncomeSourceValueModel = _(transactions)
            .filter((transaction: TransactionModel) => !transaction?.ignore && transaction?.incomeUid?.length > 0)
            .groupBy((transaction: TransactionModel) => transaction.incomeUid)
            .mapValues((transactions: TransactionModel[]) => {
                const sum = _.sumBy(transactions, 'amount');
                return sum === 0 ? 0 : -sum;
            })
            .value();
        return transactionIncomesByValue;
    }

    /** Retrieve the total of all transaction incomes */
    public static getTransactionIncomesTotal(transactions: TransactionModel[]): number {
        const transactionIncomesByValue: any = this.getTransactionIncomesByValue(transactions);
        return _(transactionIncomesByValue).values().sum();
    }

    /** Retrieve the total of all income sources */
    public static getTotalIncome(transactions: TransactionModel[], incomeSources: IncomeSourceModel[]): number {
        const transactionIncomesTotal: number = this.getTransactionIncomesTotal(transactions);
        const manualIncomesTotal: number = this.getManualIncomesTotal(incomeSources);
        return transactionIncomesTotal + manualIncomesTotal;
    }

    /** Retrieve the post tax total of all income sources, monthly */
    public static totalPostTaxMonthlyIncome(
        transactions: TransactionModel[],
        incomeSources: IncomeSourceModel[]
    ): number {
        const totalAnnualIncome = this.getTotalIncome(transactions, incomeSources);
        return totalAnnualIncome / 12;
    }

    /** Retrieve the post tax total of manual income sources, monthly */
    public static totalPostTaxManualMonthlyIncome(incomeSources: IncomeSourceModel[]): number {
        const totalManualAnnualIncome = this.getManualIncomesTotal(incomeSources);
        return totalManualAnnualIncome / 12;
    }

    /** Upsert income for use in budgeting micojourney */
    public static async upsertIncome({
        user,
        amount,
        existingIncome,
        nonTaxIncome
    }: UpsertIncomeParams): Promise<void> {
        const currentIncome = existingIncome || new IncomeSourceModel();
        const taxOwed: number = FinanceService.incomeTaxAmount(amount);
        currentIncome.annualizedIncome = amount - taxOwed;
        currentIncome.name = OTHER_INCOME;
        currentIncome.startDate = LocalDate.now().withDayOfYear(1);

        const attributes = user.attributes;
        attributes.preTaxIncome = amount;
        attributes.postTaxIncome = currentIncome.annualizedIncome;
        attributes.nonTaxIncome = nonTaxIncome;
        await UserService.putAttributes(attributes);

        if (existingIncome) await IncomeSourceService.updateIncomeSource(currentIncome);
        else await IncomeSourceService.createIncomeSource(currentIncome);
    }
}
